import { useEffect, useState } from 'react';

import { getUser } from '../../modules/contexts/Auth/RequireAuth';
import { useSelect } from '../../modules/contexts/Select/useSelect';
import { TermoAditivo, TipoTermoAditivo } from '../../modules/types/Aquisicao';
import { LogData } from '../../modules/types/types';
import {
  getAllTermosAditivosPorEntidade,
  handleCreate,
  handleDelete,
  handleUpdate,
} from '../../services/aquisicoes/termoAditivoService';
import { isHttpError } from '../../services/httpClient';
import theme from '../../theme';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateSimp,
  sanitizarForm,
} from '../../utils/functions';
import {
  swalAttention,
  swalAtualizacao,
  swalDelete,
  swalDeleteConfirm,
  swalErro,
  swalSave,
} from '../../utils/swal';
import BarraInferiorBotoes from '../BarraInferiorBotoes';
import BoxConteudo from '../BoxConteudo';
import CheckBoxGroupBox from '../CheckBoxGroupBox';
import CheckBoxList from '../CheckBoxList';
import CustomButton from '../CustomButton';
import CustomCol from '../CustomCol';
import CustomInputDate from '../CustomInputs/CustomInputDate';
import CustomInputText from '../CustomInputs/CustomInputText';
import CustomOffcanvas from '../CustomOffcanvas';
import CustomRow from '../CustomRow';
import CustomSelect from '../CustomSelects/CustomSelect';
import { CustomTableHeader } from '../CustomTable';
import DetailsTable from '../CustomTable/DetailsTable';
import { TableDetailsRow } from '../CustomTable/types/table.type';
import CustomTableOffcanvas from '../CustomTableOffcanvas';
import Log from '../Log/Log';
import WorkspaceBotoes from '../WorkspaceBotoes';
import WorkspaceFicha from '../WorkspaceFicha';

export type TaTableRow = {
  id: string | number;
  status: string;
  dataDOU: string;
  tiposTA: string;
  documentoSEI: string | number;
  objetoTA: string;
  [key: string]: any;
};

const headers: CustomTableHeader<TaTableRow>[] = [
  { text: '', width: 1, key: '' },
  { text: 'ID', width: 2, key: 'id' },
  { text: 'Status', width: 15, key: 'status' },
  { text: 'Data DOU', width: 15, key: 'dataDOU' },
  { text: 'Tipo de TA', width: 15, key: 'tiposTA' },
  { text: 'Nº DOC SEI', width: 15, key: 'documentoSEI' },
  { text: 'Objeto', width: 50, key: 'objetoTA' },
];

const valoresExibidos: Record<string, string> = {
  id: 'ID',
  dataRegistro: 'Data de Registro',
  dataAtualizacao: 'Última Atualização',
  dataUltimaVisualizacao: 'Última Visualização',
  dataUltimaAtualizacao: 'Última Atualização',
  nEdicoes: 'Edições',
  nVisualizacoes: 'Visualizações',
  nVisualizadores: 'Visualizadores',
  usuarioRegistro: 'Responsável pelo Registro',
  usuarioAtualizacao: 'Responsável Ult. Atualização',
  usuarioUltimaVisualizacao: 'Responsável Ult. Visualização',
  observacoes: 'Observações',

  status: 'Status',
  numeroTA: 'Número do Termo',
  dataDOU: 'Data D.O.U.',
  tipoTA: 'Tipos de Termo',
  documentoSEI: 'Nº Documento Sei',
  objetoTA: 'Objeto do Termo',
  separador: '',
  separadorLog: '',
};

interface TagOption {
  id: number;
  tag: string;
}

interface TermoAditivoComponenteProps {
  tab: string;
  id_tab: string;
}

function TermoAditivoComponente({ tab, id_tab }: TermoAditivoComponenteProps) {
  const user = getUser();
  const userID = user.sub;

  const [updating, setUpdating] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [logData, setLogData] = useState<LogData>();

  const [data, setData] = useState<TermoAditivo[]>();
  const [selectedDetailsRowId, setSelectedDetailsRowId] = useState<
    string | number | null
  >(null);
  // const [tipoTAData, setTipoTAData] = useState<TipoTermoAditivo[]>([]);

  const { getSelectDominioPaiPuro, getSelectDominioPai } = useSelect();
  const tiposTA = getSelectDominioPaiPuro(153); // Domínio Tipo Termo Aditivo
  const statusTA = getSelectDominioPai(157); // Domínio Status Termo Aditivo
  const [form, setForm] = useState<TermoAditivo>({
    id: 0,
    numeroTA: '',
    status: 0,
    documentoSEI: '',
    dataDOU: '',
    objetoTA: '',
    observacoes: '',

    tiposTANumber: [],

    tabId: parseInt(id_tab, 10),
    usuarioId: userID,
  });
  const fetchData = async () => {
    try {
      const termosAditivos = await getAllTermosAditivosPorEntidade(
        tab,
        Number(id_tab),
      );
      setData(termosAditivos);
    } catch (error) {
      console.error('Erro ao obter dados do tipo de TA:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id_tab]);

  const tipoTAOptions: TagOption[] = tiposTA.map((item) => ({
    id: item.value,
    tag: item.label,
  }));

  const tableData =
    data?.map((item: TermoAditivo) => {
      return {
        id: item.id,
        status:
          statusTA.find((opt) => opt.value === item.status)?.label || 'N/A',
        dataDOU: formatDateSimp(item.dataDOU),
        tiposTA: item.tiposTA
          ? item.tiposTA
              .map((tipoTA: TipoTermoAditivo) => tipoTA.nome)
              .join(', ')
          : '',
        documentoSEI: item.documentoSEI,
        objetoTA: item.objetoTA,
      };
    }) || [];

  const offcanvasData =
    data?.map((item: TermoAditivo) => {
      return {
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro?.nomeCompleto ?? ''),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao?.nomeCompleto ?? ''),

        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(
            item.usuarioUltimaVisualizacao?.nomeCompleto ?? '',
          ),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        separadorLog: '',
        status:
          statusTA.find((opt) => opt.value === item.status)?.label || 'N/A',
        numeroTA: item.numeroTA,
        dataDOU: formatDateSimp(item.dataDOU),
        tipoTA: item.tiposTA
          ? item.tiposTA
              .map((tipoTA: TipoTermoAditivo) => tipoTA.nome)
              .join(', ')
          : '',
        documentoSEI: item.documentoSEI,
        objetoTA: item.objetoTA,
        separador: '',
        observacoes: item.observacoes,
      };
    }) || [];

  // Funções handle
  const handleClearForm = () => {
    setForm({
      ...form,
      id: 0,
      numeroTA: '',
      documentoSEI: '',
      status: 0,
      dataDOU: '',
      objetoTA: '',
      observacoes: '',

      tiposTANumber: [],
    });
    setLogData({
      ...logData,
      id: 0,
      usuarioRegistro: '',
      usuarioAtualizacao: '',
      dataRegistro: '',
      dataAtualizacao: '',
      nEdicoes: 0,
    });
  };

  const handleShowModal = () => {
    setShowModal((prev) => !prev);
    handleClearForm();
  };

  const handleClickInserirNovo = () => {
    handleClearForm();
    setUpdating(false);
    handleShowModal();
  };

  const handleTipoTAChange = (selectedIds: number[]) => {
    setForm({ ...form, tiposTANumber: selectedIds });
  };

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    const integerValue = parseInt(value, 10);
    if (name === 'status' || name === 'numeroTA') {
      setForm((prev) => {
        const updatedForm = { ...prev, [name]: integerValue };

        return updatedForm;
      });
    } else {
      setForm((prev) => {
        const updatedForm = { ...prev, [name]: value };

        return updatedForm;
      });
    }
  };

  const handleOpenDetails = (row: any) => {
    if (!row?.id) return;
    setSelectedDetailsRowId(row.id);
  };

  const handleRowClick = (row: any) => {
    handleShowModal();
    setUpdating(true);
    const filteredItem = data?.find((item) => item.id == row.id);

    if (filteredItem) {
      setForm({
        ...filteredItem,
        numeroTA: filteredItem.numeroTA,
        status: filteredItem.status,
        documentoSEI: filteredItem.documentoSEI,
        dataDOU: filteredItem.dataDOU,
        objetoTA: filteredItem.objetoTA,
        observacoes: filteredItem.observacoes ?? 'Não informado',
        tiposTANumber:
          filteredItem.tiposTA?.map((tipoTA: TipoTermoAditivo) => tipoTA.id) ??
          [],
      });
      setLogData({
        id: filteredItem.id,
        usuarioRegistro: filteredItem.usuarioRegistro?.nomeCompleto || '',
        usuarioAtualizacao: filteredItem.usuarioAtualizacao?.nomeCompleto || '',
        dataRegistro: filteredItem.dataRegistro ?? '',
        dataAtualizacao: filteredItem.dataUltimaAtualizacao ?? '',
        nEdicoes: filteredItem.nEdicoes ?? 0,
      });
    }
  };

  const validateForm = (form: TermoAditivo): boolean => {
    const erros: string[] = [];

    if (!form.status) {
      erros.push('<b>Status</b> é obrigatório.');
    }

    if (!form.numeroTA) {
      erros.push('<b>Nº do TA</b> é obrigatório.');
    }

    if (!form.documentoSEI) {
      erros.push('<b>Nº do Documento</b> é obrigatório.');
    }

    if (!form.dataDOU) {
      erros.push('<b>Data DOU</b> é obrigatória.');
    }

    if (!form.objetoTA) {
      erros.push('<b>Objeto TA</b> é obrigatório.');
    }

    if (!form.tiposTANumber || form.tiposTANumber.length === 0) {
      erros.push('<b>Tipo de Termo Aditivo</b> é obrigatório.');
    }

    if (erros.length > 0) {
      swalAttention(erros.join('<br>'));
      return false;
    }

    return true;
  };

  const formatForm = (form: TermoAditivo) => {
    const formAjustado = {
      ...form,
      usuarioId: Number(userID),
      tabId: parseInt(id_tab, 10),
      dominioIds: form.tiposTANumber,
    };

    return formAjustado;
  };

  const handleCreateTA = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formAjustado = formatForm(form);
      const formEnvio = sanitizarForm(formAjustado);

      await handleCreate(formEnvio, tab);

      swalSave();
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (error) {
      if (isHttpError(error)) {
        swalErro(error.response?.data.message);
      }
      console.error('Erro ao enviar dados', error);
    }
  };

  const handleUpdateTA = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formAjustado = formatForm(form);
      const formToSend = sanitizarForm(formAjustado);

      const response = await handleUpdate(formToSend);

      swalAtualizacao('Sucesso', response.data.message);
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (error: any) {
      if (isHttpError(error)) {
        swalErro(error.response?.data.message);
      }
      console.error('Erro ao atualizar dados', error);
    }
  };

  const handleDeleteTA = async () => {
    const result = await swalDelete();
    if (!result) return swalErro('A operação falhou.');
    try {
      await handleDelete(form.id);
      swalDeleteConfirm();
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (error) {
      if (isHttpError(error)) {
        console.error('Erro ao excluir registro', error);
        swalErro(error.response?.data.message);
      }
    }
  };

  return (
    <BoxConteudo boxTitulo={'Termos Aditivos'} fechado>
      <CustomRow>
        <CustomCol width="100%">
          <CustomTableOffcanvas
            headers={headers}
            data={tableData}
            displayValues={valoresExibidos}
            onRowClick={handleRowClick}
            offcanvasTitle="Termo Aditivo"
            widthOffcanvas="60%"
            onOpenOffcanvas={handleOpenDetails}
            showPagination={false}
          >
            {(row) => (
              <DetailsTable
                rows={offcanvasData as TableDetailsRow[]}
                selectedId={row?.id ?? selectedDetailsRowId}
                displayValues={valoresExibidos}
              />
            )}
          </CustomTableOffcanvas>
        </CustomCol>
      </CustomRow>
      <CustomRow>
        <CustomCol className="d-flex justify-content-end">
          <CustomButton
            label="Novo Registro"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            onClick={handleClickInserirNovo}
            disabled={id_tab !== 'undefined' ? false : true}
          />
        </CustomCol>
      </CustomRow>

      {/* Modal TermoAditivo /*/}
      <CustomOffcanvas
        title={'Termo Aditivo'}
        show={showModal}
        onClose={handleShowModal}
        width="70vw"
      >
        <WorkspaceFicha>
          {/* hide Log component if register not created yet */}
          {form.id && form.id !== 0 ? (
            <Log {...logData} entity="TB_TA" id={Number(form.id)} />
          ) : null}
          <BoxConteudo boxTitulo="Identificação do TA">
            <CustomRow
              style={{ marginTop: '10px', justifyContent: 'flex-start' }}
            >
              <CustomCol width="200px">
                <CustomSelect
                  name="status"
                  options={statusTA}
                  title="Status"
                  value={form.status}
                  onChange={handleChange}
                  required
                />
              </CustomCol>

              <CustomCol width="250px">
                <CustomInputText
                  name="numeroTA"
                  title="Nº do TA"
                  value={form.numeroTA.toString()}
                  onChange={handleChange}
                  textAlign="right"
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <CustomInputText
                  name="documentoSEI"
                  title="Nº Documento SEI"
                  value={form.documentoSEI.toString()}
                  onChange={handleChange}
                  textAlign="right"
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <CustomInputDate
                  name="dataDOU"
                  title="Data DOU"
                  value={form.dataDOU}
                  onChange={handleChange}
                  required
                  // readonly={isDateReadOnly}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="objetoTA"
                  value={form.objetoTA}
                  title="Objeto TA"
                  required
                  onChange={handleChange}
                  type="textArea"
                  rows={3}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CheckBoxGroupBox title="Tipo de Termo Aditivo">
                <CheckBoxList
                  opcoes={tipoTAOptions}
                  opcoesSelecionadas={form.tiposTANumber}
                  isOpen={true}
                  onChange={handleTipoTAChange}
                  // isOpen={showModalActTA}
                />
              </CheckBoxGroupBox>
            </CustomRow>

            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="observacoes"
                  value={form.observacoes}
                  title="Observações"
                  onChange={handleChange}
                  type="textArea"
                  rows={3}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updating ? (
              <WorkspaceBotoes
                delete
                onClickDelete={handleDeleteTA}
                save
                onClickSave={handleUpdateTA}
              />
            ) : (
              <WorkspaceBotoes inserir onClickInserir={handleCreateTA} />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </BoxConteudo>
  );
}

export default TermoAditivoComponente;
